import { useState, useEffect } from 'react';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import { Link } from 'react-router-dom';
import { SEO } from '../../utils/seo';

function ProgramDownloadForm() {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const formData = new URLSearchParams();
      formData.append('name', name);
      formData.append('email', email);
      formData.append('download_type', 'Summit Program PDF');

      const response = await fetch('https://readdy.ai/api/form/d684a1lbiueuoqm1rbmg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString(),
      });

      if (response.ok) {
        setIsSuccess(true);
      } else {
        setError('Something went wrong. Please try again.');
      }
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="text-center py-4">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-500/20 flex items-center justify-center">
          <i className="ri-check-line text-3xl text-emerald-400"></i>
        </div>
        <h4 className="text-lg font-display font-bold text-white mb-2">Thank You!</h4>
        <p className="text-sm text-navy-300 mb-6">
          Click below to view and save the program as PDF.
        </p>
        <a
          href="/program/print"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-champagne-500 text-navy-900 rounded-full font-medium hover:bg-champagne-400 transition-all duration-300 cursor-pointer whitespace-nowrap"
        >
          <i className="ri-file-pdf-2-line"></i>
          View & Save as PDF
        </a>
      </div>
    );
  }

  return (
    <form
      id="program-download-form"
      data-readdy-form
      onSubmit={handleSubmit}
      className="space-y-4"
    >
      <div>
        <input
          type="text"
          name="name"
          placeholder="Your Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-navy-400 focus:outline-none focus:border-champagne-400 transition-colors duration-300 text-sm"
        />
      </div>
      <div>
        <input
          type="email"
          name="email"
          placeholder="Your Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-navy-400 focus:outline-none focus:border-champagne-400 transition-colors duration-300 text-sm"
        />
      </div>
      {error && (
        <p className="text-red-400 text-sm text-center">{error}</p>
      )}
      <button
        type="submit"
        disabled={isSubmitting}
        className="w-full flex items-center justify-center gap-2 px-6 py-3.5 bg-gradient-to-r from-champagne-500 to-champagne-600 text-navy-900 rounded-xl font-medium hover:from-champagne-400 hover:to-champagne-500 transition-all duration-300 cursor-pointer whitespace-nowrap disabled:opacity-70"
      >
        {isSubmitting ? (
          <>
            <i className="ri-loader-4-line animate-spin"></i>
            Sending...
          </>
        ) : (
          <>
            <i className="ri-download-2-line"></i>
            Get Program PDF
          </>
        )}
      </button>
      <p className="text-xs text-navy-400 text-center">
        We respect your privacy. No spam, ever.
      </p>
    </form>
  );
}

export default function ProgramPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  const [activeDay, setActiveDay] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [displayDay, setDisplayDay] = useState(0);
  const [expandedSessions, setExpandedSessions] = useState<Record<string, boolean>>({});

  const toggleSession = (dayIndex: number, sessionIndex: number) => {
    const key = `${dayIndex}-${sessionIndex}`;
    setExpandedSessions(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleDayChange = (index) => {
    if (index === activeDay || isAnimating) return;
    
    setIsAnimating(true);
    setActiveDay(index);
    
    // Wait for exit animation, then switch content and play enter animation
    setTimeout(() => {
      setDisplayDay(index);
      setTimeout(() => {
        setIsAnimating(false);
      }, 50);
    }, 300);
  };

  const programDays = [
    {
      day: 'Day One',
      theme: 'The Inner Citadel',
      subtitle: 'Cultivating the Energy, Mindset & Health that form the true capital of a woman',
      description: 'A transformative day focused on vitality, mindset mastery, abundance strategies, and building your inner foundation.',
      sessions: [
        { time: '09:00', title: 'The Vitality Welcome', type: 'Energy & Health', description: 'A luxurious morning diagnostic: bio-frequency analysis, adaptogenic elixirs, and a signature vitality shot to align your energy for the day.' },
        { time: '09:45', title: 'The Sacred Opening', type: 'Mindset & Ritual', description: 'A ceremonial welcome grounding us in the belief that true wealth is a state of being. Introduction to the "Code of Abundance."' },
        { time: '10:30', title: 'Keynote: The Anatomy of Abundance', type: 'Mindset & Strategy', description: 'A masterclass on the inner architecture of wealth: merging ancient wisdom with modern neuroscience to rewire for prosperity.' },
        { time: '11:30', title: 'The Roundtable: Nomads of Luxury', type: 'Lifestyle & Access', description: 'An intimate conversation on the geopolitics of taste. How global women move capital, culture, and influence across borders.' },
        { time: '12:30', title: 'Networking Apéritif', type: 'Social Life & Networking', description: 'A curated champagne reception to connect with fellow sovereign women.' },
        { time: '14:00', title: 'The Confidential: Your Family Constitution', type: 'Strategy & Legacy', description: 'A private, high-stakes workshop: dry-practice only—of drafting the charter that protects your values and your bloodline\'s future.' },
        { time: '19:00', title: 'The Sanctuary: Breath as Asset', type: 'Energy & Vitality', description: 'A guided breathwork session to reset the nervous system and unlock a new idea of clarity. Your health is your first and greatest wealth.' },
      ],
      color: 'from-navy-800 to-navy-900',
      bgColor: 'bg-navy-50',
      textColor: 'text-navy-700',
    },
    {
      day: 'Day Two',
      theme: 'The Sovereign Expression',
      subtitle: 'Curating Your Lifestyle, Influence & Legacy on the World Stage',
      description: 'Express your sovereignty through personal branding, lifestyle curation, and legacy building on the global stage.',
      sessions: [
        { time: '09:00', title: 'The Bio-Optimization Ritual', type: 'Energy & Vitality', description: 'A morning masterclass in cellular capital: hormonal yoga flow followed by a private cryotherapy experience to ignite your radiance.' },
        { time: '10:00', title: 'Masterclass: The Wealth/Longevity Nexus', type: 'Health & Strategy', description: 'Presenting health as the ultimate luxury asset. Exploring the science of longevity and how to invest in your most precious capital: you.' },
        { time: '11:00', title: 'The Influence Salon: The Identity Asset', type: 'Image & Personal Branding', description: 'A sophisticated dialogue on personal branding as an intangible asset: how to curate your image to open doors and command rooms.' },
        { time: '12:30', title: 'Curated Collectors\' Luncheon', type: 'Lifestyle & Networking', description: 'An exclusive seated luncheon where conversations flow as freely as the champagne, set amongst a private viewing of collectors\' pieces.' },
        { time: '14:30', title: 'The Concierge Workshop: The Art of Access', type: 'Lifestyle & Access', description: 'Unlocking your VIP life: How to leverage concierge services for the ultimate bespoke experience—from securing a private yacht in the Aegean to a last-minute table at Noma.' },
        { time: '16:30', title: 'The Grand Finale: From Fortune to Felicity', type: 'Legacy & Fulfillment', description: 'A closing fireside chat on the synthesis of wealth: aligning your capital, your culture, and your values to build a legacy of true fulfillment.' },
        { time: '17:30', title: 'The Sovereign Closing Reception', type: 'Social Life & Networking', description: 'A toast to the new sovereign: of wealth. An opportunity to solidify the bonds forged over two days in an intimate sunset setting.' },
        { time: '19:30', title: 'Gala Dinner', type: 'Celebration', description: 'An evening of elegance at one of Beirut\'s most exclusive venues. Black tie optional.' },
      ],
      color: 'from-champagne-500 to-champagne-600',
      bgColor: 'bg-champagne-50',
      textColor: 'text-champagne-700',
    },
  ];

  const experiences = [
    {
      title: 'Closed Door Investment Circles',
      description: 'Intimate discussions on capital allocation',
      icon: 'ri-door-closed-line',
    },
    {
      title: 'Governance Architecture Labs',
      description: 'Hands-on family framework workshops',
      icon: 'ri-building-4-line',
    },
    {
      title: 'Beirut Cultural Immersions',
      description: 'Art, history, and local culture',
      icon: 'ri-palette-line',
    },
    {
      title: 'Private Salons',
      description: 'Exclusive intimate conversations',
      icon: 'ri-chat-private-line',
    },
  ];
  
  const schema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Summit Program",
    "description": "Explore the two-day program of the Women In Wealth Summit featuring capital discussions, legacy governance, and lifestyle design.",
    "about": {
      "@type": "Event",
      "name": "Women In Wealth Summit",
      "eventSchedule": programDays.map(day => ({
        "@type": "Schedule",
        "name": day.theme,
        "description": day.description
      }))
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Summit Program | Women In Wealth · Beirut 2026"
        description="Explore the two-day program of the Women In Wealth Summit in Beirut. Day 1: Capital & Legacy. Day 2: Identity & Lifestyle. Curated conversations, frameworks, and experiences."
        keywords="summit program, women in wealth program, summit agenda, summit schedule, capital allocation, legacy governance, family office governance, wealth management program"
        canonical={`${siteUrl}/program`}
        schema={schema}
      />
      <Navigation />

      {/* Hero - Minimal Editorial */}
      <section className="pt-32 pb-16 px-8 lg:px-16 xl:px-24 bg-gradient-to-br from-navy-900 via-navy-800 to-navy-900 overflow-hidden relative">
        {/* Decorative gradient orbs */}
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-champagne-600/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-blush-500/10 rounded-full blur-3xl"></div>
        
        <div className="max-w-7xl mx-auto relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <p className="text-champagne-400 text-sm uppercase tracking-[0.4em] mb-6 font-medium">
                March 2026 · Beirut
              </p>
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-display font-bold text-white leading-[1.05] mb-6">
                Two Days of Transformation
              </h1>
              <p className="text-xl text-navy-200 leading-relaxed">
                Curated conversations, frameworks, and experiences designed to transform how you think about capital, legacy, and lifestyle.
              </p>
            </div>
            <div className="relative flex justify-center lg:justify-end">
              <div className="relative w-full max-w-md lg:max-w-lg">
                {/* Decorative circles */}
                <div className="absolute -top-8 -left-8 w-32 h-32 rounded-full bg-gradient-to-br from-champagne-500/20 to-blush-500/20 blur-2xl"></div>
                <div className="absolute -bottom-6 -right-6 w-40 h-40 rounded-full bg-gradient-to-br from-champagne-400/30 to-blush-400/20 blur-2xl"></div>
                
                {/* Main illustration */}
                <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                  <img
                    src="https://public.readdy.ai/ai/img_res/c3cda2bf9e7a7d76f58dc7fee521cc6f.jpg"
                    alt="Transformation Journey"
                    className="w-full h-auto object-cover object-top"
                  />
                </div>
                
                {/* Floating accent elements */}
                <div className="absolute -top-4 right-8 w-20 h-20 rounded-2xl bg-white/90 backdrop-blur-sm shadow-lg flex items-center justify-center">
                  <i className="ri-calendar-event-line text-3xl text-champagne-600"></i>
                </div>
                <div className="absolute -bottom-4 left-8 w-24 h-24 rounded-2xl bg-white/90 backdrop-blur-sm shadow-lg flex items-center justify-center">
                  <span className="text-3xl font-display font-bold text-navy-900">2</span>
                  <span className="text-sm text-navy-500 ml-1">Days</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Two-Day Program Layout */}
      <section className="py-24 px-8 lg:px-16 xl:px-24 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-16">
            <p className="text-champagne-600 text-sm uppercase tracking-[0.3em] mb-4 font-medium">
              The Journey
            </p>
            <h2 className="text-4xl md:text-5xl font-display font-bold text-slate-900 mb-6">
              Your Two-Day Transformation
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Each day is thoughtfully designed to build upon the last, creating a complete journey of inner and outer transformation.
            </p>
          </div>

          {/* Two Column Day Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
            {programDays.map((day, dayIndex) => (
              <div key={dayIndex} className="relative">
                {/* Day Header Card */}
                <div className={`relative rounded-3xl overflow-hidden mb-6 ${dayIndex === 0 ? 'bg-gradient-to-br from-navy-800 to-navy-900' : 'bg-gradient-to-br from-champagne-500 to-champagne-600'}`}>
                  <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%23ffffff%22%20fill-opacity%3D%220.05%22%3E%3Cpath%20d%3D%22M36%2034v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6%2034v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6%204V0H4v4H0v2h4v4h2V6h4V4H6z%22%2F%3E%3C%2Fg%3E%3C%2Fg%3E%3C%2Fsvg%3E')] opacity-50"></div>
                  <div className="relative p-8 lg:p-10">
                    <div className="flex items-center gap-3 mb-4">
                      <span className={`px-3 py-1 backdrop-blur-sm rounded-full text-sm font-medium ${dayIndex === 0 ? 'bg-white/20 text-white/90' : 'bg-navy-900/20 text-navy-900/90'}`}>
                        {day.day}
                      </span>
                    </div>
                    <h3 className={`text-2xl lg:text-3xl font-display font-bold mb-3 ${dayIndex === 0 ? 'text-white' : 'text-navy-900'}`}>
                      {day.theme}
                    </h3>
                    <p className={`text-sm lg:text-base leading-relaxed ${dayIndex === 0 ? 'text-white/80' : 'text-navy-900/70'}`}>
                      {day.subtitle}
                    </p>
                  </div>
                </div>

                {/* Sessions List */}
                <div className="space-y-3">
                  {day.sessions.map((session, sessionIndex) => {
                    const isExpanded = expandedSessions[`${dayIndex}-${sessionIndex}`];
                    return (
                      <div
                        key={sessionIndex}
                        className={`group relative bg-white rounded-2xl border border-slate-100 hover:border-slate-200 hover:shadow-lg transition-all duration-300 overflow-hidden cursor-pointer ${
                          isExpanded ? 'shadow-lg border-slate-200' : ''
                        }`}
                        onClick={() => toggleSession(dayIndex, sessionIndex)}
                      >
                        {/* Left accent line */}
                        <div className={`absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b ${day.color} ${isExpanded ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'} transition-opacity duration-300`}></div>
                        
                        <div className="p-5 lg:p-6">
                          <div className="flex items-start gap-4">
                            {/* Time Badge */}
                            <div className={`flex-shrink-0 w-16 h-16 rounded-xl ${day.bgColor} flex flex-col items-center justify-center`}>
                              <span className={`text-lg font-bold ${day.textColor}`}>{session.time.split(':')[0]}</span>
                              <span className={`text-xs ${day.textColor} opacity-70`}>{session.time.split(':')[1]}</span>
                            </div>
                            
                            {/* Content */}
                            <div className="flex-1 min-w-0">
                              <h4 className={`text-base lg:text-lg font-display font-bold transition-colors duration-300 ${
                                isExpanded ? 'text-slate-700' : 'text-slate-900 group-hover:text-slate-700'
                              }`}>
                                {session.title}
                              </h4>
                              <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${day.bgColor} ${day.textColor} mt-1.5`}>
                                {session.type}
                              </span>
                            </div>

                            {/* Expand Icon */}
                            <div className={`w-8 h-8 flex items-center justify-center flex-shrink-0 rounded-full transition-all duration-300 ${
                              isExpanded 
                                ? `${day.bgColor} ${day.textColor}` 
                                : 'bg-slate-100 text-slate-400 group-hover:bg-slate-200'
                            }`}>
                              <i className={`ri-arrow-down-s-line text-lg transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}></i>
                            </div>
                          </div>

                          {/* Expandable Description */}
                          <div className={`overflow-hidden transition-all duration-300 ease-in-out ${
                            isExpanded ? 'max-h-40 opacity-100 mt-4' : 'max-h-0 opacity-0'
                          }`}>
                            <div className="pt-4 border-t border-slate-100">
                              <p className="text-slate-600 text-sm lg:text-base leading-relaxed">
                                {session.description}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gala Dinner Feature */}
      <section className="relative py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Elegant%20luxury%20gala%20dinner%20setting%20with%20sophisticated%20table%20arrangements%20candlelight%20warm%20ambient%20lighting%20refined%20aesthetic%20high%20end%20venue%20Mediterranean%20style%20beautiful%20evening%20atmosphere&width=1800&height=800&seq=gala-new-001&orientation=landscape"
            alt="Gala Dinner"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-slate-900/80 to-slate-900/60"></div>
        </div>

        <div className="relative z-10 px-8 lg:px-16 xl:px-24">
          <div className="max-w-7xl mx-auto">
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-3 px-5 py-2.5 bg-white/10 backdrop-blur-sm rounded-full mb-8">
                <i className="ri-goblet-line text-champagne-400"></i>
                <span className="text-champagne-300 text-sm font-medium">Evening Celebration</span>
              </div>
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold text-white mb-6">
                Gala Dinner
              </h2>
              <p className="text-xl text-slate-300 leading-relaxed mb-8">
                An evening of elegance, connection, and celebration in one of Beirut's most exclusive venues. Where meaningful conversations continue under the stars.
              </p>
              <div className="flex flex-wrap gap-6 text-slate-400">
                <div className="flex items-center gap-2">
                  <i className="ri-calendar-line text-champagne-400"></i>
                  <span>Day Two Evening</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-map-pin-line text-champagne-400"></i>
                  <span>Exclusive Venue</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-shirt-line text-champagne-400"></i>
                  <span>Black Tie Optional</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Signature Experiences Grid */}
      <section className="py-24 px-8 lg:px-16 xl:px-24 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-champagne-600 text-sm uppercase tracking-[0.3em] mb-4 font-medium">
              Beyond the Program
            </p>
            <h2 className="text-4xl md:text-5xl font-display font-bold text-slate-900">
              Signature Experiences
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {experiences.map((exp, index) => (
              <div
                key={index}
                className="group bg-white rounded-2xl p-8 border border-slate-100 hover:border-champagne-200 hover:shadow-xl transition-all duration-500 hover:-translate-y-1 text-center"
              >
                <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-champagne-100 to-champagne-50 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <i className={`${exp.icon} text-2xl text-champagne-600`}></i>
                </div>
                <h3 className="text-lg font-display font-bold text-slate-900 mb-2">
                  {exp.title}
                </h3>
                <p className="text-sm text-slate-600">
                  {exp.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Download Program PDF Section */}
      <section className="py-24 px-8 lg:px-16 xl:px-24 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="relative bg-gradient-to-br from-navy-900 via-navy-800 to-navy-900 rounded-3xl overflow-hidden">
            {/* Decorative elements */}
            <div className="absolute top-0 right-0 w-96 h-96 bg-champagne-500/10 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-80 h-80 bg-blush-500/10 rounded-full blur-3xl"></div>
            
            <div className="relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-12 p-10 lg:p-16">
              {/* Left - Content */}
              <div className="flex flex-col justify-center">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full w-fit mb-6">
                  <i className="ri-file-pdf-2-line text-champagne-400"></i>
                  <span className="text-champagne-300 text-sm font-medium">Program Guide</span>
                </div>
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-display font-bold text-white mb-6">
                  Download the Full Program
                </h2>
                <p className="text-lg text-navy-200 leading-relaxed mb-8">
                  Get the complete summit program with detailed session descriptions, speaker profiles, venue information, and exclusive insights into what awaits you in Beirut.
                </p>
                <div className="flex flex-wrap gap-6 text-navy-300">
                  <div className="flex items-center gap-2">
                    <i className="ri-calendar-check-line text-champagne-400"></i>
                    <span>Full Schedule</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <i className="ri-user-star-line text-champagne-400"></i>
                    <span>Speaker Bios</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <i className="ri-map-2-line text-champagne-400"></i>
                    <span>Venue Details</span>
                  </div>
                </div>
              </div>

              {/* Right - Download Form */}
              <div className="flex items-center justify-center">
                <div className="w-full max-w-md bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/10">
                  <div className="text-center mb-6">
                    <div className="w-20 h-20 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-champagne-500 to-champagne-600 flex items-center justify-center shadow-lg">
                      <i className="ri-download-cloud-2-line text-3xl text-white"></i>
                    </div>
                    <h3 className="text-xl font-display font-bold text-white mb-2">
                      Get Your Copy
                    </h3>
                    <p className="text-sm text-navy-300">
                      Enter your email to receive the program PDF
                    </p>
                  </div>

                  <ProgramDownloadForm />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-8 lg:px-16 xl:px-24 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-display font-bold text-slate-900 mb-6">
            Secure Your Place
          </h2>
          <p className="text-lg text-slate-600 mb-8">
            Participation is by application only, curated by Racha Mezher.
          </p>
          <Link
            to="/attend"
            className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-slate-900 text-white rounded-full font-medium hover:bg-slate-800 transition-all duration-300 cursor-pointer whitespace-nowrap group"
          >
            Request an Invitation
            <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform duration-300"></i>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
